<!-- Header -->
<div class="flex items-center justify-between p-6 border-b border-gray-200">
  <h2 class="text-2xl font-bold text-gray-900">Forest Description Report</h2>
  <button
    type="button"
    class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
    [mat-dialog-close]="false"
  >
    <i class="fas fa-times text-xl"></i>
  </button>
</div>

<!-- Content -->
<div class="p-6 max-h-[70vh] overflow-y-auto">
  <!-- Tabs Navigation -->
  <div class="border-b border-gray-200 mb-6">
    <nav class="-mb-px flex space-x-8">
      @for (report of reports; track reports; let i = $index) {
        <button
          class="py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
          [ngClass]= "{'border-green-500 text-green-600': i === activeTab,
          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300': i !== activeTab }"
          (click)="activeTab = i">
          <i class="fas mr-2" [class]="i === 0 ? 'fa-house-damage' : 'fa-vector-square'"></i>
          {{ report.target.name }}
        </button>
      }
    </nav>
  </div>

  <!-- Tab Content -->
  @for (report of reports; track report; let i = $index) {
    <div [class.hidden]="activeTab !== i">
      <!-- Report Header -->
      <div class="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 mb-6">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ report.target.name }}</h3>
            <p class="text-sm text-gray-600">
              <i class="fas fa-map-marked-alt mr-2"></i>
              Reporting Area: <span class="font-medium">{{ report.target.area | round:1 }} hectares</span>
            </p>
          </div>
          <div class="flex flex-col sm:flex-row gap-3">
            <button
              (click)="viewOnMap(report.target)"
              class="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <i class="fas fa-map mr-2"></i>
              View on Map
            </button>
            <button
              (click)="export(report)"
              class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <i class="fas fa-download mr-2"></i>
              Export PDF
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Statistics -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h4 class="text-lg font-semibold text-gray-900 mb-4">Summary Statistics</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex justify-between items-center py-3 border-b border-gray-100">
            <span class="text-sm font-medium text-gray-600">Total Recoverable Volume (TRV):</span>
            <div class="flex items-center gap-2">
            <span class="text-sm font-semibold text-gray-900">
              <span *ngIf="report.report.estimatedTotalRecoverableVolume > 0">
                {{ report.report.estimatedTotalRecoverableVolume | round:1 | numberFormat }}
              </span>
              <span *ngIf="report.report.estimatedTotalRecoverableVolume <= 0" class="text-gray-500">
                No native forest mapped
              </span>
            </span>
              <button
                (click)="showDisclaimer()"
                class="text-amber-500 hover:text-amber-600 transition-colors duration-200"
                title="View disclaimer"
              >
                <i class="fas fa-exclamation-triangle"></i>
              </button>
            </div>
          </div>
          <div class="flex justify-between items-center py-3 border-b border-gray-100">
            <span class="text-sm font-medium text-gray-600">% Sawlog:</span>
            <span class="text-sm font-semibold text-gray-900">{{ report.report.sawLog | round:1 }}%</span>
          </div>
          <div class="flex justify-between items-center py-3 border-b border-gray-100">
            <span class="text-sm font-medium text-gray-600">% Industrial Grade Peeler Log:</span>
            <span class="text-sm font-semibold text-gray-900">{{ report.report.industrialGradePeeler | round:1 }}%</span>
          </div>
          <div class="flex justify-between items-center py-3">
            <span class="text-sm font-medium text-gray-600">% Pulp Quality Log:</span>
            <span class="text-sm font-semibold text-gray-900">{{ report.report.pulpQualityLog | round:1 }}%</span>
          </div>
        </div>
      </div>

      <!-- Forest Classes Table -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200">
          <h4 class="text-lg font-semibold text-gray-900">Forest Classes</h4>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Forest Class Code
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Forest Class Description
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Area (ha)
              </th>
            </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
            <tr *ngFor="let element of report.report.forestClasses" class="hover:bg-gray-50 transition-colors duration-150">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ element.fc_Code }}
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">
                {{ element.fc_Desc }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ element.area | round:1 }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  }
</div>

<!-- Footer -->
<div class="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
  <button
    type="button"
    class="px-6 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
    [mat-dialog-close]="false"
  >
    Close
  </button>
</div>
